import * as ActionTypes from "constants/ActionTypes";
import fetch from "isomorphic-fetch";
import generateId from "utils/generateId";
import normalizeStory from "utils/normalizeStory";
import parsePlayerOptions from "utils/parsePlayerOptions";
import postprocessNormalizedStory from "utils/postprocessNormalizedStory";
import Promise from "bluebird";
import cloneDeep from "lodash/cloneDeep";
import { storyLoaded } from "actions/StoryActions";
import { storyPublishSettingsLoaded } from "actions/StoryPublishSettingsActions";

import { call, put, takeEvery } from "redux-saga/effects";

const options = parsePlayerOptions(
  {},
  {
    windowLocationSearch: window.location.search,
    offline: false,
  },
);

let lastStoryId; // This can be a global since only one story should be loaded at once.

const simulateResponseRegex = /(?:custom|simulated?)-?responses?-([^/]+)/i;

function parseStoryId(storySlug) {
  const storyIdRegex = /^([\w]*-)?(\d+)-.*$/;
  const match = storyIdRegex.exec(storySlug);

  if (!match && isSimulatedSource()) {
    return 112;
  }

  return match ? Number(match[2]) : undefined;
}

function* handleConditionalLoadStory(action) {
  const newStorySlug = window.offline
    ? "123-offline"
    : action.routeData.params.storySlug;

  if (!newStorySlug) {
    yield put(storyLoaded(null));
  } else {
    const storyId = parseStoryId(newStorySlug);
    const needsToLoad = lastStoryId !== storyId;
    lastStoryId = storyId;

    if (needsToLoad) {
      const story = yield call(loadStory, newStorySlug);
      yield put(storyLoaded(story));
      const storyPublishSettings = yield call(
        loadStoryPublishSettings,
        storyId,
      );
      if (!storyPublishSettings.error) {
        yield put(storyPublishSettingsLoaded(storyPublishSettings));
      }
    }
  }
}

function isSimulatedSource() {
  return simulateResponseRegex.test(window.location.pathname);
}

async function loadStory(storyId) {
  const url = window.offline ? "./story.json" : `/story/${storyId}/json/`;
  return Promise.try(async () => {
    const effectivePathname = window.location.pathname;
    if (window.story) {
      return Promise.resolve({
        ...window.story,
        isSimulatedSource: true,
      });
    }

    if (simulateResponseRegex.test(effectivePathname)) {
      const simulateRegexResult = effectivePathname.match(
        simulateResponseRegex,
      );
      if (!simulateRegexResult) {
        throw new Error(
          "Should not be trying to pull simulated response when regex failed",
        );
      }
      const [, simulatedFilename] = simulateRegexResult;

      return {
        ...(await import(`simulatedResponses/${simulatedFilename}.json`)),
        isSimulatedSource: true,
      };
    }
    return fetch(url, {
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    }).then((response) => response.json());
  }).then(async (originalResponse) => {
    const response = cloneDeep(originalResponse);

    // Generate a `hotspots` key on all chapters and assign a `localId` to
    // cuepoints and hotspots. This will let us use the original Alt data and
    // still make lookups in the normalized data.
    response.chapterStore = response.chapterStore.map((chapter) => {
      const cuepoints = chapter.cuepoints.map((cuepoint) => ({
        ...cuepoint,
        localId: cuepoint.id || generateId(),
      }));

      return {
        ...chapter,
        cuepoints,
        hotspots: cuepoints,
      };
    });

    let normalizedStory = normalizeStory(response);
    try {
      normalizedStory = await postprocessNormalizedStory(normalizedStory);
    } catch (e) {
      console.error("loadStorySaga", e); //eslint-disable-line no-console
    }

    // These are overrides that can come from params. Use the normalized story since it is safe
    if (options.disableSharing) {
      normalizedStory.customizations.playerNavigation.allowSharing = false;
      delete options.disableSharing;
    }
    if (options.hideHamburger) {
      normalizedStory.customizations.playerNavigation.showHamburger = false;
      delete options.hideHamburger;
    }

    return normalizedStory;
  });
}

function loadStoryPublishSettings(storyId) {
  const settingsUrl = `/api/v1/stories/${storyId}/story_publish_settings/`;
  return fetch(settingsUrl, {
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  })
    .then((data) => data.json())
    .then((body) => ({
      ...options,
      customTwitterMessage: body.custom_twitter_message || null,
      embedCode: body.embed_code,
      storySharingUrl: body.story_sharing_url,
    }));
}

export default function* loadStorySaga() {
  yield takeEvery(ActionTypes.SET_ROUTE_DATA, handleConditionalLoadStory);
}
